/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { FunctionComponent, useEffect, useRef, useState } from 'react';

import { DeviceModel } from '../../../../core/device';
import { InteractiveComponents } from '../../../../core/enums';
import { CursorManager } from '../../../../core/navigation';
import { RootState, useTivoSelector } from '../../../../core/tivo/tivo-redux-wrapper';
import Caret from '../../Caret/Caret';
import Mousetrap from '../../Mousetrap/Mousetrap';

import './BWInteractiveLayer.scss';

interface IBWInteractiveLayerProps {
    middleLayerHeight: number;
}

const BWInteractiveLayer: FunctionComponent<IBWInteractiveLayerProps> = ({
    middleLayerHeight,
}): JSX.Element => {
    const [isCursorVisible, setIsCursorVisible] = useState(CursorManager.isCursorAppeared());
    const containerRef = useRef<HTMLDivElement>(null);
    const topLayerRef = useRef<HTMLDivElement>(null);
    const middleLayerRef = useRef<HTMLDivElement>(null);
    const bottomLayerRef = useRef<HTMLDivElement>(null);
    const leftLayerRef = useRef<HTMLDivElement>(null);
    const rightLayerRef = useRef<HTMLDivElement>(null);

    const { activeComponent } = useTivoSelector((state: RootState) => state.interactiveLayer);
    const { horizontalItem, verticalItems, verticalPosition } = useTivoSelector(
        (state: RootState) => state.biaxialWidget,
    );

    useEffect(() => {
        const cursorManagerRef = CursorManager.subscribe(onCursorStateChangeCallback);

        return () => {
            CursorManager.unsubscribe(cursorManagerRef);
        };
    }, []);

    useEffect(() => {
        const bottomLayerHeight =
            (containerRef.current?.offsetHeight || 0) -
            middleLayerHeight -
            (topLayerRef.current?.offsetHeight || 0);
        const rightLayerWidth =
            (containerRef.current?.offsetWidth || 0) -
            (leftLayerRef.current?.offsetWidth || 0) -
            (middleLayerRef.current?.offsetWidth || 0);

        bottomLayerRef.current?.style.setProperty('height', `${bottomLayerHeight}px`);
        rightLayerRef.current?.style.setProperty('width', `${rightLayerWidth}px`);
    }, [middleLayerHeight]);

    const onCursorStateChangeCallback = (state) => {
        setIsCursorVisible(DeviceModel.isWebOsPlatform() && state);
    };

    const isInteractiveLayerVisible = verticalItems.length > 0 && isCursorVisible;

    const isHorizontalCaretActive = horizontalItem.listItems.length > 1;
    const isNorthCaretActive =
        verticalPosition > 0 && activeComponent === InteractiveComponents.NORTH;
    const isSouthCaretActive =
        verticalPosition < verticalItems.length - 1 &&
        activeComponent === InteractiveComponents.SOUTH;

    return (
        <div
            className={`interactive-layer-container${!isInteractiveLayerVisible ? ' not-visible' : ''}`}
            ref={containerRef}
        >
            <Mousetrap
                value={InteractiveComponents.NORTH}
                className="top-vertical-layer"
                ref={topLayerRef}
            >
                <Caret
                    direction={InteractiveComponents.NORTH}
                    visible={isNorthCaretActive}
                    active={isNorthCaretActive}
                    className="caret-north"
                />
            </Mousetrap>
            <Mousetrap
                value={'OK'}
                className="middle-vertical-layer"
                ref={middleLayerRef}
                style={{ height: middleLayerHeight }}
            ></Mousetrap>
            <Mousetrap
                value={InteractiveComponents.SOUTH}
                className="bottom-vertical-layer"
                ref={bottomLayerRef}
            >
                <Caret
                    direction={InteractiveComponents.SOUTH}
                    visible={isSouthCaretActive}
                    active={isSouthCaretActive}
                    className="caret-south"
                />
            </Mousetrap>
            <Mousetrap
                value={InteractiveComponents.WEST}
                className="left-horizontal-layer"
                ref={leftLayerRef}
                style={{ height: middleLayerHeight }}
            >
                <Caret
                    direction={InteractiveComponents.WEST}
                    visible={activeComponent === InteractiveComponents.WEST}
                    active={isHorizontalCaretActive}
                    className="caret-west"
                />
            </Mousetrap>
            <Mousetrap
                value={InteractiveComponents.EAST}
                className="right-horizontal-layer"
                ref={rightLayerRef}
                style={{ height: middleLayerHeight }}
            >
                <Caret
                    direction={InteractiveComponents.EAST}
                    visible={activeComponent === InteractiveComponents.EAST}
                    active={isHorizontalCaretActive}
                    className="caret-east"
                />
            </Mousetrap>
        </div>
    );
};

export default BWInteractiveLayer;
